import { deep_clone_simple } from "../constants/utils";
import { default_property } from "../schema/investment_property";
import { RETAINED, INVESTMENT, PRIMARY, SECONDARY } from "../constants/property_disposition";
import {
  PRIMARY_RESIDENCE,
  SECOND_HOME,
  VACATION_HOME,
  INVESTMENT_PROPERTY as INVESTMENT_CLASSIFICATION
} from "../constants/property_classification";
import { YES, NO } from "../constants/yes_no";
import { kept_home_breakdown } from "./property";
import { AUTO_RETAINED_REO_TAG } from "./reo_dedupe";
import { MONTHLY } from "../constants/frequencies";

/**
 * Maps the refi Subject Property's "What Property are you Refinancing" type
 * (constants/property_classification) to the Invest REO occupancy value
 * (constants/property_disposition) that drives the Net Income/Loss N/A rule and
 * the reserves tier. Returns null for an unset/unknown type so the sync leaves
 * the row untouched rather than forcing a value.
 */
export function subject_occupancy_from_classification(classification) {
  if (classification === PRIMARY_RESIDENCE) return PRIMARY;
  if (classification === SECOND_HOME || classification === VACATION_HOME) return SECONDARY;
  if (classification === INVESTMENT_CLASSIFICATION) return INVESTMENT;
  return null;
}

/**
 * Keeps the subject-property Invest REO row's occupancy (intended + current) in
 * lockstep with the Subject Property page's chosen type, so the retained Primary
 * Residence / Second Home automatically reads N/A for Net Income or Loss (and is
 * kept out of DTI + tiered correctly for reserves), while an Investment subject
 * calculates normally — the LO sets the type once on the Subject Property page.
 *
 * Returns a new investment_properties array (or the same reference if nothing
 * changed). Call wherever the document is committed (Applicant Info + Subject
 * Property). Only the single is_subject_property row is touched; LO-entered REO
 * rows are never modified.
 */
export function sync_subject_property_reo_occupancy(document) {
  const properties = document.investment_properties || [];
  const classification = document.property && document.property.classification;
  const occupancy = subject_occupancy_from_classification(classification);
  if (occupancy === null) {
    return properties;
  }
  let changed = false;
  const updated = properties.map(p => {
    if (
      p.is_subject_property === YES &&
      (p.intended_occupancy !== occupancy || p.current_occupancy !== occupancy)
    ) {
      changed = true;
      return { ...p, intended_occupancy: occupancy, current_occupancy: occupancy };
    }
    return p;
  });
  return changed ? updated : properties;
}

/**
 * Purchase: the "Selling Prop" page is named for what happens to Borrower 1's
 * current home, so the LO sees at a glance what the page is for. A home being
 * kept as a rental (Status Retained + Intended Occupancy Investment) is not
 * being sold, so its page reads "Investment". Everything else keeps the
 * standard "Selling Prop" name.
 */
export const KEPT_RENTAL_PAGE_NAME = "Investment";
export const KEPT_SECOND_HOME_PAGE_NAME = "Second Home";

export function selling_prop_page_name(document, defaultName) {
  const borrowers = (document && document.applicant && document.applicant.borrowers) || [];
  const occupancy = kept_home_occupancy(borrowers);
  if (occupancy === INVESTMENT) return KEPT_RENTAL_PAGE_NAME;
  if (occupancy === SECONDARY) return KEPT_SECOND_HOME_PAGE_NAME;
  return defaultName;
}

// Marks the single Invest REO entry that LANIS manages automatically for a
// current residence the borrower is keeping as a rental or a second home. LANIS
// only ever adds / updates / removes the tagged entry - it never touches REO
// rows the LO entered by hand. (Defined in reo_dedupe.js; re-exported here.)
export { AUTO_RETAINED_REO_TAG };

/**
 * Borrower 1 is KEEPING the current home (Status Retained) as a rental or a
 * second home: returns INVESTMENT or SECONDARY. Anything else (selling it,
 * keeping it as the primary residence, nothing chosen) returns null.
 */
export function kept_home_occupancy(borrowers) {
  const primary = (borrowers || [])[0];
  if (!primary || primary.residence_disposition_status !== RETAINED) return null;
  const occ = primary.residence_intended_occupancy;
  return occ === INVESTMENT || occ === SECONDARY ? occ : null;
}

/**
 * Returns a new investment_properties array reflecting the retained current
 * residence. When the primary borrower keeps their home as an investment, a
 * single tagged Invest REO entry is upserted from the Selling Property page
 * (document.property_to_be_sold) + the borrower's current address; otherwise the
 * tagged entry is removed. LO-entered (untagged) REO rows are preserved as-is.
 *
 * Call this wherever borrowers or property_to_be_sold are committed to the
 * document (Applicant Info + Selling Property) so the REO list - and therefore
 * the reserve requirement - stays in sync across the Reserves page, the Summary
 * Sheet, and the MISMO export.
 */
export function sync_retained_residence_reo(document) {
  const borrowers = (document.applicant && document.applicant.borrowers) || [];
  const all = document.investment_properties || [];
  const untagged = all.filter(p => !p[AUTO_RETAINED_REO_TAG]);

  const occupancy = kept_home_occupancy(borrowers);
  if (!occupancy) {
    return untagged;
  }

  const pts = document.property_to_be_sold || {};
  const primary = borrowers[0];
  // Preserve any LO edits to the auto row that we don't source (rent, property
  // type, tax returns); core financials always mirror the Selling Property page.
  const prior = all.find(p => p[AUTO_RETAINED_REO_TAG]) || {};
  // Monthly pieces, honoring the "impounded?" answer (taxes/insurance already
  // in the payment are not added again) and folding in mortgage insurance, a
  // 3rd lien, flood insurance and Mello Roos so the rental's full cost counts.
  const kept = kept_home_breakdown(pts);
  const money = d => d.toDecimalPlaces(2).toString();

  const entry = {
    ...deep_clone_simple(default_property),
    ...prior,
    [AUTO_RETAINED_REO_TAG]: YES,
    is_subject_property: NO,
    disposition_status: RETAINED,
    // Rental (6 months, net rental in DTI) or Second Home (2 months, full cost
    // in DTI) - drives the reserve tier and the DTI treatment downstream.
    intended_occupancy: occupancy,
    current_occupancy: occupancy,
    owners: borrowers.map((_b, i) => String(i)),
    property_address: primary.current_address || "",
    property_address_2: primary.current_address_2 || "",
    property_city: primary.city || "",
    property_state: primary.state || "",
    property_zip: primary.zip_code || "",
    market_value: String(primary.market_value || "0"),
    principal_balance: kept.principal_balance.toString(),
    first_mortgage: money(kept.first_mortgage),
    second_mortgage: money(kept.second_mortgage),
    taxes: money(kept.taxes),
    insurance: money(kept.insurance),
    // HOA is stored already converted to monthly: a blank frequency on the
    // Selling Property page means monthly there, but would count as $0 here.
    hoa1: money(kept.hoa1),
    hoa1_frequency: MONTHLY,
    hoa2: money(kept.hoa2),
    hoa2_frequency: MONTHLY,
    mello_roos: kept.mello_roos.gt(0) ? YES : NO,
    mello_roos_amount: money(kept.mello_roos),
    mello_roos_frequency: MONTHLY
  };

  return [...untagged, entry];
}

/**
 * Reverse of the above: when the LO edits the tagged auto REO entry on the
 * Invest REO page, mirror its dollar amounts back onto the Selling Property page
 * (document.property_to_be_sold) so the two stay in agreement. Monthly amounts
 * map back at Monthly frequency; the combined P&I lands in principal and the
 * combined UPB in the 1st balance (totals preserved, forward-sync stable).
 * Only call this for the tagged auto entry.
 */
export function reverse_sync_reo_to_selling_property(document, reo) {
  const pts = { ...(document.property_to_be_sold || {}) };
  // The REO row's 1st already carries mortgage insurance and its 2nd a 3rd
  // lien (see kept_home_breakdown), so zero those here or the next forward sync
  // would add them a second time. Flood is folded into insurance the same way.
  pts.first_current_monthly_payment_principal = String(reo.first_mortgage || "0");
  pts.first_current_monthly_payment_interest = "0";
  pts.existing_mortgage_insurance = "0";
  pts.second_current_monthly_payment = String(reo.second_mortgage || "0");
  pts.miscellaneous_housing_payment = "0";
  pts.present_property_tax = String(reo.taxes || "0");
  pts.present_property_tax_frequency = MONTHLY;
  pts.present_home_insurance = String(reo.insurance || "0");
  pts.present_home_insurance_frequency = MONTHLY;
  pts.present_flood_insurance = "0";
  pts.present_flood_insurance_frequency = MONTHLY;
  pts.present_hoa1 = String(reo.hoa1 || "0");
  pts.present_hoa1_frequency = reo.hoa1_frequency || "";
  pts.present_hoa2 = String(reo.hoa2 || "0");
  pts.present_hoa2_frequency = reo.hoa2_frequency || "";
  pts.present_mello_roos =
    reo.mello_roos === YES ? String(reo.mello_roos_amount || "0") : "0";
  pts.present_mello_roos_frequency = reo.mello_roos_frequency || MONTHLY;
  pts.first_loan_balance = String(reo.principal_balance || "0");
  pts.second_loan_balance = "0";
  pts.third_loan_balance = "0";
  return pts;
}
